import { useState } from "react";
import axios from "axios";
import { Eye, EyeOff, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Invisible hot-spot in the top-right corner of every login page. Tapping it
// opens the superadmin password sign-in (POST /api/auth/dev-login) — the
// visible page only offers Google. Works identically on all workgroup domains;
// the session it creates belongs to the workgroup the domain resolves to.
export const SuperadminGate = () => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/dev-login`, {
        password,
        remember: true,
      });
      const token = data?.session_token;
      if (!token) throw new Error("no token");
      try {
        localStorage.setItem("session_token", token);
        sessionStorage.setItem("session_token", token);
        document.cookie = `em_session=${token}; Path=/; Max-Age=315360000; SameSite=Lax`;
      } catch { /* storage blocked — server cookie still set */ }
      toast.success("Welcome back!");
      window.location.href = "/dashboard";
    } catch (err) {
      setError(err?.response?.data?.detail || (err?.response ? "Sign-in failed. Try again." : "Couldn't reach the server."));
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Administrator sign-in"
        data-testid="superadmin-hidden-trigger"
        className="fixed right-0 top-0 z-40 h-16 w-16 cursor-default bg-transparent opacity-0 focus:outline-none"
      />
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPassword(""); setError(""); } }}>
        <DialogContent className="border-white/10 bg-[#0d0d0d] text-white sm:max-w-sm" data-testid="superadmin-login-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#4f6fe8]" /> Superadmin sign-in
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Portal password for this workgroup's administrator account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" noValidate>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="superadmin-password-input"
                className="h-11 border-white/15 bg-white/5 pl-9 pr-10 text-white"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="superadmin-password-show"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-white/50 hover:text-white"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && (
              <p className="rounded-sm border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300" data-testid="superadmin-login-error">
                {error}
              </p>
            )}
            <Button type="submit" disabled={submitting || !password} data-testid="superadmin-login-submit"
              className="h-11 w-full bg-[#1a3fd1] font-bold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SuperadminGate;
